// Portfolio › Focus (FOUNDER-OS; 12 §Focus layer): the founder's cockpit. "Needs you" with its
// count and the portfolio priorities strip, then one row per company — exceptions first and loud
// (a red or amber edge, the needs-you chips), healthy companies quiet. Each row: name and relation,
// health, KPI and trend, priorities on / at risk / off, escalations, budget vs actual, agents,
// headcount and the next meeting; a click opens the company's drill-down. No company yet: "Add
// your first company" (`accounts.companies.add`, from the switcher's list).

import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  CountPill,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  Input,
  LocationHeader,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BuildingIcon, PlusIcon, TelescopeIcon, UnlinkIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { FocusRow, MeResponse, PortfolioFocus } from "../../api/types";
import { SwitcherTile } from "../../shell/company-switcher";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { LoadError, Loading } from "../screen-parts";
import { ROW_HOVER } from "../settings/row-actions";
import { useSession } from "../use-feature";
import {
  BudgetBar,
  Figure,
  formatSpend,
  headcountText,
  KpiCell,
  NeedsChips,
  RelationBadge,
  relativeDay,
  useAgencyNames,
} from "./portfolio-parts";
import {
  focusQuery,
  meCompaniesQuery,
  portfolioKeys,
  useCanEditPortfolio,
} from "./portfolio-queries";

export function FocusScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const focus = useQuery(focusQuery(api, company.id));
  const { me } = useSession();
  const [adding, setAdding] = useState(false);
  // An owner of the whole account (a grant limited to some companies reads only).
  const owner = useCanEditPortfolio(api, company.id);
  const data = focus.data;
  const qc = useQueryClient();
  const unlink = useMutation({
    mutationFn: (companyId: string) =>
      api.client.portfolio.companies.remove({
        params: { co: company.id, company_id: companyId },
      }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: portfolioKeys.root(company.id) }),
        qc.invalidateQueries({ queryKey: ["me", "companies"] }),
      ]),
  });
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="portfolio-focus">
      <LocationHeader
        icon={TelescopeIcon}
        crumbs={[{ label: company.name }]}
        title="Focus"
        actions={
          owner ? (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <PlusIcon aria-hidden="true" />
              Add company
            </Button>
          ) : null
        }
      />
      <div className="flex min-w-0 flex-col gap-5 px-6 py-5 max-md:px-4">
        {focus.isPending ? (
          <Loading label="Loading the portfolio" rows={4} />
        ) : focus.isError ? (
          <LoadError what="The portfolio" error={focus.error} />
        ) : data && data.companies.length === 0 ? (
          <EmptyState
            icon={BuildingIcon}
            title="Add your first company"
            description="Bring in a company you own or serve through your agency: its numbers, priorities and escalations rise here."
            actions={
              owner ? (
                <Button onClick={() => setAdding(true)}>
                  <PlusIcon aria-hidden="true" />
                  Add company
                </Button>
              ) : null
            }
          />
        ) : data ? (
          <>
            <NeedsYouHeader focus={data} slug={company.slug} />
            <ul className="flex flex-col gap-2.5" aria-label="Companies">
              {data.companies.map((r) => (
                <li key={r.company_id} className="group/row relative">
                  <FocusCard row={r} slug={company.slug} />
                  {owner ? (
                    // A sibling of the card's link (no button inside a link), over its corner.
                    <span className="absolute top-2 right-2">
                      <ConfirmRowAction
                        icon={UnlinkIcon}
                        label={`Remove ${r.name} from the portfolio`}
                        tip="Remove from portfolio"
                        className={cn(ROW_HOVER, "bg-card")}
                        disabled={unlink.isPending}
                        title={`Remove ${r.name} from the portfolio?`}
                        description="It leaves Focus, and its linked seats and goal links are removed. Nothing in the company itself changes; add it back any time."
                        confirmLabel="Remove"
                        onConfirm={() => unlink.mutate(r.company_id)}
                      />
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            {unlink.error ? <LoadError what="Removing the company" error={unlink.error} /> : null}
          </>
        ) : null}
      </div>
      <AddCompanyDialog
        api={api}
        me={me}
        co={company.id}
        held={new Set(data?.companies.map((r) => r.company_id) ?? [])}
        open={adding}
        onOpenChange={setAdding}
      />
    </div>
  );
}

function NeedsYouHeader({ focus, slug }: { focus: PortfolioFocus; slug: string }) {
  const p = focus.portfolio_priorities;
  const needing = focus.companies.filter((r) => r.needs_you.length > 0).length;
  return (
    <section
      aria-labelledby="needs-you"
      className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <h2 id="needs-you" className="flex items-center gap-2 font-heading text-lg font-semibold">
          Needs you
          <CountPill
            count={focus.needs_you_count}
            tone={focus.needs_you_count ? "warning" : "neutral"}
            aria-label={`${focus.needs_you_count} things`}
          />
        </h2>
        <p className="text-sm text-muted-foreground">
          {focus.needs_you_count === 0
            ? `All quiet across ${focus.companies.length} companies.`
            : `${needing} of ${focus.companies.length} companies have something for you. Only exceptions rise here.`}
        </p>
      </div>
      <Link
        to="/c/$slug/kpis/priorities"
        params={{ slug }}
        data-slot="portfolio-priorities"
        aria-label={`Portfolio priorities ${p.quarter}: ${p.on} on track, ${p.at_risk} at risk, ${p.off} off track`}
        className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm shadow-xs hover:bg-muted"
      >
        <span className="font-medium text-foreground">Portfolio priorities</span>
        <span className="text-muted-foreground">{p.quarter}</span>
        <Badge variant="success">{p.on} on track</Badge>
        {p.at_risk ? <Badge variant="warning">{p.at_risk} at risk</Badge> : null}
        {p.off ? <Badge variant="danger">{p.off} off</Badge> : null}
      </Link>
    </section>
  );
}

function FocusCard({ row, slug }: { row: FocusRow; slug: string }) {
  const agencies = useAgencyNames();
  const loud = row.needs_you.length > 0;
  const red = row.needs_you.some(
    (n) => n.kind === "kpi" || n.kind === "budget" || n.kind === "escalation",
  );
  const p = row.priorities;
  return (
    <Link
      to="/c/$slug/portfolio/companies/$companyId"
      params={{ slug, companyId: row.company_id }}
      aria-label={`${row.name}: ${loud ? `${row.needs_you.length} things need you` : "all quiet"}`}
      data-slot="focus-row"
      data-company={row.slug}
      data-loud={loud || undefined}
      className={cn(
        "group block rounded-lg border border-border bg-card px-4 py-3 shadow-xs transition-colors hover:border-strong hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        loud && "border-l-[3px]",
        loud && (red ? "border-l-destructive" : "border-l-warning"),
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pr-8">
        <SwitcherTile name={row.name} portfolio={false} />
        <span className="font-heading text-[15px] font-semibold text-foreground">{row.name}</span>
        <RelationBadge relation={row.relation} agency={agencies.get(row.company_id)} />
        <KpiCell row={row} />
        <div className="ml-auto max-md:ml-0 max-md:w-full">
          {loud ? (
            <NeedsChips needs={row.needs_you} />
          ) : (
            <span className="text-xs text-muted-foreground">All quiet</span>
          )}
        </div>
      </div>
      <div
        className={cn(
          "mt-3 grid grid-cols-6 gap-x-5 gap-y-3 border-t border-subtle pt-3 max-lg:grid-cols-3 max-sm:grid-cols-2",
          !loud && "opacity-80",
        )}
      >
        <Figure label="Priorities">
          <span className="tabular-nums">
            <span className="text-foreground">{p.on}</span> on
            {p.at_risk ? <span className="text-warning-text"> · {p.at_risk} at risk</span> : null}
            {p.off ? <span className="font-medium text-danger-text"> · {p.off} off</span> : null}
            {p.total === 0 ? " · none set" : null}
          </span>
        </Figure>
        <Figure label="Escalations">
          {row.escalations.count ? (
            <span className="font-medium text-danger-text" title={row.escalations.items[0]?.title}>
              {row.escalations.count} open
            </span>
          ) : (
            "None"
          )}
        </Figure>
        <Figure label="Budget (month)">
          <BudgetBar finance={row.finance} />
        </Figure>
        <Figure label="Agents">
          <span className="tabular-nums">
            {row.agents.live} live · {row.agents.runs_7d} runs 7d ·{" "}
            {formatSpend(row.agents.spend_month_micros)}
            {row.agents.approvals_waiting ? (
              <span className="text-info-text"> · {row.agents.approvals_waiting} to approve</span>
            ) : null}
          </span>
        </Figure>
        <Figure label="Headcount">
          <span className="tabular-nums">{headcountText(row.headcount)}</span>
        </Figure>
        <Figure label="Next meeting">
          {row.meetings.next_at ? relativeDay(row.meetings.next_at) : "None scheduled"}
        </Figure>
      </div>
    </Link>
  );
}

/** "Add company": the companies the founder owns or serves that are not in the portfolio yet. */
export function AddCompanyDialog({
  api,
  me,
  co,
  held,
  open,
  onOpenChange,
}: {
  api: Api;
  me: MeResponse;
  co: string;
  held: ReadonlySet<string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const list = useQuery({ ...meCompaniesQuery(api), enabled: open });
  const accountId = me.portfolio?.account_id ?? list.data?.account?.id ?? null;
  const add = useMutation({
    mutationFn: (companyId: string) => {
      if (!accountId) throw new Error("No account");
      return api.client.accounts.companies.add({
        params: { account_id: accountId },
        body: { company_id: companyId },
      });
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: portfolioKeys.root(co) }),
        qc.invalidateQueries({ queryKey: ["me", "companies"] }),
      ]),
  });
  // Wave 12: a new company the founder owns, made and added in one call.
  const [newName, setNewName] = useState("");
  const create = useMutation({
    mutationFn: (name: string) => {
      if (!accountId) throw new Error("No account");
      return api.client.accounts.companies.add({
        params: { account_id: accountId },
        body: { create: { name } },
      });
    },
    onSuccess: async () => {
      setNewName("");
      await Promise.all([
        qc.invalidateQueries({ queryKey: portfolioKeys.root(co) }),
        qc.invalidateQueries({ queryKey: ["me", "companies"] }),
        qc.invalidateQueries({ queryKey: ["me"] }),
      ]);
      onOpenChange(false);
    },
  });
  const canAdd = (list.data?.companies ?? []).filter(
    (c) =>
      c.kind !== "portfolio" &&
      !held.has(c.company_id) &&
      (c.group === "owned" || c.group === "client"),
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a company</DialogTitle>
          <DialogDescription>
            A company you own, or one you serve through your agency. Nothing in it changes: its
            numbers rise into Focus from its own projections.
          </DialogDescription>
        </DialogHeader>
        {list.isPending ? (
          <Loading label="Loading your companies" rows={2} />
        ) : canAdd.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Every company you can add is already here.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {canAdd.map((c) => (
              <li
                key={c.company_id}
                className="flex items-center gap-2.5 rounded-md border border-border px-3 py-2"
              >
                <SwitcherTile name={c.name} portfolio={false} />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.name}</span>
                <RelationBadge
                  relation={c.group === "client" ? "client" : "owned"}
                  agency={c.agency_name}
                />
                <Button
                  size="sm"
                  variant="outline"
                  aria-label={`Add ${c.name}`}
                  disabled={add.isPending}
                  onClick={() => add.mutate(c.company_id)}
                >
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
        <form
          className="flex flex-col gap-2 border-t border-border pt-4"
          data-slot="new-company-form"
          onSubmit={(e) => {
            e.preventDefault();
            const name = newName.trim();
            if (name && !create.isPending) create.mutate(name);
          }}
        >
          <Field>
            <FieldLabel htmlFor="new-company-name">Or start a new company</FieldLabel>
            <div className="flex gap-2">
              <Input
                id="new-company-name"
                value={newName}
                placeholder="Company name"
                maxLength={120}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button type="submit" disabled={!newName.trim() || create.isPending}>
                Create
              </Button>
            </div>
          </Field>
          <p className="text-xs text-muted-foreground">
            You own it and hold its CEO seat until you hire one. It uses this portfolio's currency
            and calendar.
          </p>
          {create.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {create.error instanceof ApiError && create.error.status === 409
                ? "A company with that name exists. Try another name."
                : "The company could not be created."}
            </p>
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
